import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { CalendarCheck, Download, Megaphone, RefreshCw, Search, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { SEMINAR } from "@/seminars";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { adminApi, formatApiError } from "@/lib/api";
import { isAdTagged, trafficSource } from "@/lib/utils";
import { RegistrationDialog } from "./RegistrationDialog";

export const STATUSES = ["new", "contacted", "confirmed", "attended", "cancelled"];
const STATUS_STYLE = {
  new: "bg-[#f2d28f]/15 text-[#f2d28f] border-[#f2d28f]/30",
  contacted: "bg-sky-400/15 text-sky-200 border-sky-400/30",
  confirmed: "bg-emerald-400/15 text-emerald-200 border-emerald-400/30",
  attended: "bg-violet-400/15 text-violet-200 border-violet-400/30",
  cancelled: "bg-red-400/15 text-red-200 border-red-400/30",
};

const ALL = "__all__";

const ATTENDANCE_STYLE = {
  attended: "bg-emerald-400/15 text-emerald-200 border-emerald-400/30",
  no_show: "bg-amber-400/15 text-amber-200 border-amber-400/30",
};

export function AttendanceBadge({ status, minutes }) {
  if (!status || status === "pending") return null;
  const label = status === "attended" ? (minutes ? `Attended · ${minutes}m` : "Attended") : "No-show";
  return (
    <Badge variant="outline" className={`text-[10px] font-bold uppercase tracking-wide ${ATTENDANCE_STYLE[status] || ""}`} data-testid={`attendance-badge-${status}`}>
      {label}
    </Badge>
  );
}

export function StatusBadge({ status }) {
  return <Badge variant="outline" className={`text-[10px] font-bold uppercase tracking-wide ${STATUS_STYLE[status] || ""}`}>{status}</Badge>;
}

export const FOLLOWUP_LABEL = { thank_you_sent: "Thank-you sent", missed_you_sent: "Missed-you sent", failed: "Follow-up failed", skipped: "Follow-up skipped" };

export function FollowupBadge({ status }) {
  if (!FOLLOWUP_LABEL[status]) return null;
  const ok = status.endsWith("_sent");
  return (
    <Badge variant="outline" className={`text-[10px] font-bold uppercase tracking-wide ${ok ? "border-sky-400/30 bg-sky-400/15 text-sky-200" : "border-red-400/30 bg-red-400/15 text-red-200"}`} data-testid={`followup-badge-${status}`}>
      {FOLLOWUP_LABEL[status]}
    </Badge>
  );
}

export function AdSourceBadge({ reg }) {
  if (!isAdTagged(reg)) return null;
  const label = reg.utm_campaign ? `${reg.utm_source || "facebook"} · ${reg.utm_campaign}` : trafficSource(reg);
  return (
    <span className="mt-1 inline-flex max-w-[180px] items-center gap-1 truncate rounded-full border border-fuchsia-400/30 bg-fuchsia-400/10 px-2 py-0.5 text-[10px] font-semibold text-fuchsia-200" title={`${trafficSource(reg)}${reg.utm_campaign ? ` · ${reg.utm_campaign}` : ""}${reg.utm_content ? ` · ${reg.utm_content}` : ""}`} data-testid={`ad-source-badge-${reg.id}`}>
      <Megaphone className="h-3 w-3 shrink-0" /> <span className="truncate">{label}</span>
    </span>
  );
}

export function RegistrationsTable({ onChanged }) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [language, setLanguage] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const [ref, setRef] = useState(ALL);
  const [traffic, setTraffic] = useState(ALL);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);

  const params = {
    page,
    limit: 25,
    ...(debounced && { search: debounced }),
    ...(language !== ALL && { language }),
    ...(status !== ALL && { status }),
    ...(ref !== ALL && { ref }),
    ...(traffic !== ALL && { traffic }),
  };

  const query = useQuery({
    queryKey: ["admin-registrations", params],
    queryFn: async () => (await adminApi.get("/admin/registrations", { params })).data,
    keepPreviousData: true,
  });
  const links = useQuery({ queryKey: ["admin-links"], queryFn: async () => (await adminApi.get("/admin/links")).data });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["admin-registrations"] });
    onChanged?.();
  };

  const update = useMutation({
    mutationFn: ({ id, body }) => adminApi.patch(`/admin/registrations/${id}`, body),
    onSuccess: () => { toast.success("Registration updated"); invalidate(); },
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });
  const remove = useMutation({
    mutationFn: (id) => adminApi.delete(`/admin/registrations/${id}`),
    onSuccess: () => { toast.success("Registration deleted"); invalidate(); },
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });
  const resend = useMutation({
    mutationFn: (id) => adminApi.post(`/admin/registrations/${id}/resend`),
    onSuccess: () => toast.success("Zapier + email notifications re-queued"),
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });
  const syncAttendance = useMutation({
    mutationFn: () => adminApi.post("/admin/attendance/sync"),
    onSuccess: (res) => {
      const d = res.data || {};
      if (d.status === "zoom_disabled") {
        toast.error("Zoom is not connected, so attendance can't be synced");
        return;
      }
      const totals = d.totals || {};
      const synced = totals.synced_sessions || 0;
      const pending = (d.sessions || []).filter((s) => s.status === "not_occurred").length;
      if (synced === 0) {
        toast.message("No webinars have run yet", { description: `${pending} upcoming session(s). Attendance will be available after each event.` });
      } else {
        const queued = d.followup_targets || 0;
        toast.success(`Attendance synced · ${totals.attended} attended, ${totals.no_show} no-show across ${synced} session(s)`, {
          description: queued ? `Sending ${queued} follow-up email(s) in the background.` : "Follow-up emails already sent for these registrants.",
        });
      }
      invalidate();
    },
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });
  const runFollowups = useMutation({
    mutationFn: () => adminApi.post("/admin/followups/run"),
    onSuccess: (res) => {
      const d = res.data || {};
      if (d.status === "nothing_to_send") {
        toast.message("Nothing to send", { description: "Every synced attendee / no-show already has a follow-up. Sync attendance first for new sessions." });
      } else if (!d.email_enabled) {
        toast.warning(`${d.targets} follow-up(s) queued but email is not configured`, { description: "Set RESEND_API_KEY to actually deliver them." });
      } else {
        toast.success(`Sending ${d.targets} follow-up email(s)`, { description: "Thank-you emails to attendees, sorry-we-missed-you to no-shows. Refresh in a moment to see statuses." });
      }
      setTimeout(invalidate, 2500);
    },
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });
  const followupOne = useMutation({
    mutationFn: (id) => adminApi.post(`/admin/registrations/${id}/followup`),
    onSuccess: (res) => {
      const s = res.data?.status;
      if (s === "skipped") toast.warning("Follow-up skipped: email not configured (RESEND_API_KEY)");
      else if (s === "failed") toast.error("Follow-up email failed. Check backend logs");
      else toast.success(s === "thank_you_sent" ? "Thank-you email sent" : "Sorry-we-missed-you email sent");
      invalidate();
    },
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });

  const exportCsv = async () => {
    try {
      const { data } = await adminApi.get("/admin/registrations/export", { params: { ...params, page: undefined, limit: undefined }, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${SEMINAR.id}-registrations-${dayjs().format("YYYYMMDD-HHmm")}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (err) {
      toast.error("Export failed");
    }
  };

  const data = query.data;
  const selectClass = "h-10 w-full rounded-xl border-white/15 bg-white/5 text-xs sm:w-40";

  return (
    <div className="admin-card p-5 sm:p-6" data-testid="registrations-panel">
      <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="relative w-full lg:max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
          <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Search name, email, mobile or campaign" className="h-10 rounded-xl border-white/15 bg-white/5 pl-10 text-sm" data-testid="reg-search-input" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={language} onValueChange={(v) => { setLanguage(v); setPage(1); }}>
            <SelectTrigger className={selectClass} data-testid="reg-filter-language"><SelectValue placeholder="Language" /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>All languages</SelectItem><SelectItem value="English">English</SelectItem><SelectItem value="Chinese">Chinese</SelectItem></SelectContent>
          </Select>
          <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
            <SelectTrigger className={selectClass} data-testid="reg-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>All statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={ref} onValueChange={(v) => { setRef(v); setPage(1); }}>
            <SelectTrigger className={selectClass} data-testid="reg-filter-ref"><SelectValue placeholder="Referral" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All sources</SelectItem>
              <SelectItem value="direct">Direct / organic</SelectItem>
              {(links.data?.items || []).map((l) => <SelectItem key={l.code} value={l.code}>{l.code} · {l.advisor_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={traffic} onValueChange={(v) => { setTraffic(v); setPage(1); }}>
            <SelectTrigger className={selectClass} data-testid="reg-filter-traffic"><SelectValue placeholder="Traffic" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All traffic</SelectItem>
              <SelectItem value="ads">From ads (UTM / Facebook)</SelectItem>
              <SelectItem value="organic">No ad tag</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => syncAttendance.mutate()} disabled={syncAttendance.isPending} className="h-10 rounded-xl border-white/15 bg-white/5 text-xs hover:bg-white/10" data-testid="sync-attendance-btn">
            <CalendarCheck className="mr-1.5 h-3.5 w-3.5" /> {syncAttendance.isPending ? "Syncing…" : "Sync attendance"}
          </Button>
          <Button variant="outline" size="sm" onClick={() => { if (window.confirm("Send follow-up emails to every synced attendee (thank-you) and no-show (sorry we missed you) who hasn't received one yet?")) runFollowups.mutate(); }} disabled={runFollowups.isPending} className="h-10 rounded-xl border-white/15 bg-white/5 text-xs hover:bg-white/10" data-testid="send-followups-btn">
            <Send className="mr-1.5 h-3.5 w-3.5" /> {runFollowups.isPending ? "Queuing…" : "Send follow-ups"}
          </Button>
          <Button variant="outline" size="sm" onClick={exportCsv} className="h-10 rounded-xl border-white/15 bg-white/5 text-xs hover:bg-white/10" data-testid="export-csv-btn">
            <Download className="mr-1.5 h-3.5 w-3.5" /> Export CSV
          </Button>
        </div>
      </div>

      <Table data-testid="registrations-table">
        <TableHeader>
          <TableRow className="border-white/10 hover:bg-transparent">
            <TableHead className="text-white/50">Registrant</TableHead>
            <TableHead className="text-white/50">Session</TableHead>
            <TableHead className="text-white/50">Question</TableHead>
            <TableHead className="text-white/50">Referral · Ad source</TableHead>
            <TableHead className="text-white/50">Status</TableHead>
            <TableHead className="text-white/50">Registered</TableHead>
            <TableHead className="text-right text-white/50">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {query.isLoading && (
            <TableRow><TableCell colSpan={7} className="py-10 text-center text-sm text-white/50">Loading registrations…</TableCell></TableRow>
          )}
          {data && data.items.length === 0 && (
            <TableRow><TableCell colSpan={7} className="py-10 text-center text-sm text-white/50" data-testid="registrations-empty">No registrations match these filters.</TableCell></TableRow>
          )}
          {data?.items.map((reg) => (
            <TableRow key={reg.id} className="cursor-pointer border-white/8 hover:bg-white/5" onClick={() => setSelected(reg)} data-testid={`registration-row-${reg.id}`}>
              <TableCell className="py-3">
                <div className="text-sm font-semibold">{reg.full_name}</div>
                <div className="text-xs text-white/50">{reg.email}</div>
                <div className="text-xs text-white/50">{reg.mobile}</div>
              </TableCell>
              <TableCell className="text-xs">
                <div>{reg.event_date}</div>
                <div className="mt-1 flex flex-wrap items-center gap-1">
                  <Badge variant="outline" className="border-white/15 text-[10px] text-[#f2d28f]">{reg.event_language}</Badge>
                  <AttendanceBadge status={reg.attendance_status} minutes={reg.attended_duration_minutes} />
                  <FollowupBadge status={reg.followup_status} />
                </div>
              </TableCell>
              <TableCell className="max-w-[220px] truncate text-xs text-white/70" title={reg.main_question}>{reg.main_question || "–"}</TableCell>
              <TableCell className="text-xs">
                {reg.ref_code ? <span><span className="font-bold text-[#f2d28f]">{reg.ref_code}</span><br /><span className="text-white/50">{reg.referrer_name || "Unknown link"}</span></span> : <span className="text-white/40">Direct</span>}
                <AdSourceBadge reg={reg} />
              </TableCell>
              <TableCell onClick={(e) => e.stopPropagation()}>
                <Select value={reg.status} onValueChange={(value) => update.mutate({ id: reg.id, body: { status: value } })}>
                  <SelectTrigger className="h-8 w-[130px] rounded-lg border-white/15 bg-white/5 text-xs" data-testid={`reg-status-select-${reg.id}`}><StatusBadge status={reg.status} /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </TableCell>
              <TableCell className="text-xs text-white/60">{dayjs(reg.created_at).format("DD MMM YYYY")}<br />{dayjs(reg.created_at).format("HH:mm")}</TableCell>
              <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-end gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-white/60 hover:text-white" title="Re-send Zapier + email notifications" onClick={() => resend.mutate(reg.id)} data-testid={`reg-resend-btn-${reg.id}`}><RefreshCw className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-300/70 hover:text-red-200" title="Delete" onClick={() => { if (window.confirm(`Delete registration for ${reg.full_name}?`)) remove.mutate(reg.id); }} data-testid={`reg-delete-btn-${reg.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {data && data.pages > 1 && (
        <div className="mt-4 flex items-center justify-between text-xs text-white/60">
          <span data-testid="reg-pagination-info">Page {data.page} of {data.pages} · {data.total} total</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-full border-white/15 bg-white/5 text-xs" data-testid="reg-prev-page">Previous</Button>
            <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)} className="rounded-full border-white/15 bg-white/5 text-xs" data-testid="reg-next-page">Next</Button>
          </div>
        </div>
      )}

      <RegistrationDialog
        registration={selected}
        onClose={() => setSelected(null)}
        onSave={(body) => update.mutate({ id: selected.id, body }, { onSuccess: () => setSelected(null) })}
        onFollowup={() => followupOne.mutate(selected.id, { onSuccess: () => setSelected(null) })}
        followupPending={followupOne.isPending}
      />
    </div>
  );
}
